import { AUTH_REJECTED_CODE } from "@/types/auth.ts";
import { getErrorStatus, getRetryDelayMs, isCancellation } from "./retryPolicy.ts";

export type AuthFailureVerdict = "rejected" | "indeterminate" | "other";

function getResponseData(error: unknown): unknown {
  if (typeof error !== "object" || error === null) return undefined;

  const response = (error as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) return undefined;

  return (response as { data?: unknown }).data;
}

function hasAuthRejectedCode(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { code?: unknown }).code === AUTH_REJECTED_CODE
  );
}

export function classifyAuthFailure(error: unknown): AuthFailureVerdict {
  if (isCancellation(error)) return "other";

  const status = getErrorStatus(error);
  if (status === 401) {
    return hasAuthRejectedCode(getResponseData(error)) ? "rejected" : "indeterminate";
  }
  if (status === undefined || status === 429 || status >= 500) return "indeterminate";
  return "other";
}

export function isAuthRejection(error: unknown): boolean {
  return classifyAuthFailure(error) === "rejected";
}

export function isRetryableSocketAuthError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const data = (error as { data?: unknown }).data;
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { retryable?: unknown }).retryable === true
  );
}

/** Socket middleware failures bypass Socket.IO's transport reconnect loop. */
export function getSocketAuthRetryDelayMs(
  failureCount: number,
  random: () => number = Math.random,
): number {
  return Math.max(1_000, getRetryDelayMs(failureCount, undefined, random));
}
